import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, IconButton, InputAdornment, MenuItem, TextField, Tooltip } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import { PageHeader } from '../components/PageHeader';
import { AppDataGrid } from '../components/AppDataGrid';
import { ConfirmDialog, FormDialog } from '../components/Dialogs';
import { useAuth } from '../contexts/AuthContext';
import { errorMessage, formatDateTime, parseUtc, testsApi, usersApi, type Test, type User } from '../lib/api';

const number = (v: unknown) => (v === null || v === undefined ? '—' : String(v));

const columns: GridColDef<Test>[] = [
  { field: 'tested_at', headerName: 'Tested', width: 170, valueFormatter: (v) => formatDateTime(v as string) },
  { field: 'serial', headerName: 'Serial', width: 110 },
  { field: 'stage', headerName: 'Stage', width: 70, valueFormatter: (v) => v ?? '—' },
  { field: 'user_name', headerName: 'User', flex: 1, minWidth: 140 },
  { field: 'station', headerName: 'Station', width: 90, type: 'number' },
  { field: 'pressure1', headerName: 'Pressure 1 (mb)', width: 130, type: 'number', valueFormatter: number },
  { field: 'pressure2', headerName: 'Pressure 2 (mb)', width: 130, type: 'number', valueFormatter: number },
  { field: 'pressure3', headerName: 'Pressure 3 (mb)', width: 130, type: 'number', valueFormatter: number },
  { field: 'voltage', headerName: 'Voltage (V)', width: 110, type: 'number', valueFormatter: number },
  { field: 'atmospheric_pressure', headerName: 'Atmos. (hPa)', width: 110, type: 'number', valueFormatter: number },
];

const NUMBER_FIELDS = [
  ['pressure1', 'Pressure 1 (mb)'],
  ['pressure2', 'Pressure 2 (mb)'],
  ['pressure3', 'Pressure 3 (mb)'],
  ['voltage', 'Voltage (V)'],
  ['atmospheric_pressure', 'Atmospheric (hPa)'],
] as const;

type NumberField = (typeof NUMBER_FIELDS)[number][0];

// Text inputs hold strings; a blank number means not measured.
type TestForm = { user: string; barcode: string; station: string; tested_at: string } & Record<NumberField, string>;

// UTC from the API as local time for a datetime-local input, e.g. 2026-10-21T10:30:00.
function toLocalInput(utc: string): string {
  const d = parseUtc(utc);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

const text = (v: number | null) => (v === null ? '' : String(v));
const orNull = (v: string) => (v.trim() === '' ? null : Number(v));

function toForm(t: Test): TestForm {
  return {
    user: t.user_name,
    barcode: `${t.stage ?? ''}${t.serial}`,
    station: String(t.station),
    tested_at: toLocalInput(t.tested_at),
    pressure1: text(t.pressure1),
    pressure2: text(t.pressure2),
    pressure3: text(t.pressure3),
    voltage: text(t.voltage),
    atmospheric_pressure: text(t.atmospheric_pressure),
  };
}

export function Tests() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState<Test[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [serial, setSerial] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [editing, setEditing] = useState<Test | null>(null);
  const [form, setForm] = useState<TestForm | null>(null);
  const [deleting, setDeleting] = useState<Test | null>(null);

  const load = useCallback(async (filter: string) => {
    setLoading(true);
    try {
      setRows(await testsApi.list(filter.trim() || undefined));
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  // Wait for typing to pause before searching.
  useEffect(() => {
    const timer = setTimeout(() => void load(serial), 300);
    return () => clearTimeout(timer);
  }, [load, serial]);

  const startEdit = async (test: Test) => {
    try {
      setUsers(await usersApi.list());
      setForm(toForm(test));
      setEditing(test);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const allColumns: GridColDef<Test>[] = !isAdmin
    ? columns
    : [
        ...columns,
        {
          field: 'actions',
          headerName: '',
          width: 96,
          sortable: false,
          renderCell: ({ row }) => (
            <Box>
              <Tooltip title="Edit">
                <IconButton size="small" aria-label={`Edit test ${row.serial}`} onClick={() => void startEdit(row)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" aria-label={`Delete test ${row.serial}`} onClick={() => setDeleting(row)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ),
        },
      ];

  return (
    <>
      <PageHeader
        title="Tests"
        subtitle="Results sent from the workstations, newest first"
        actions={
          <>
            <TextField
              size="small"
              placeholder="Serial"
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void load(serial)}>
              Refresh
            </Button>
          </>
        }
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <AppDataGrid
        rows={rows}
        columns={allColumns}
        loading={loading}
        localeText={{ noRowsLabel: serial ? 'No tests for that serial' : 'No tests yet' }}
      />

      <FormDialog
        open={editing !== null}
        title="Edit test"
        onClose={() => setEditing(null)}
        onSubmit={async () => {
          if (!editing || !form) return;
          await testsApi.update(editing.id, {
            user: form.user,
            barcode: form.barcode,
            station: Number(form.station),
            tested_at: new Date(form.tested_at).toISOString(),
            pressure1: orNull(form.pressure1),
            pressure2: orNull(form.pressure2),
            pressure3: orNull(form.pressure3),
            voltage: orNull(form.voltage),
            atmospheric_pressure: orNull(form.atmospheric_pressure),
          });
          await load(serial);
        }}
      >
        {form && (
          <>
            <TextField
              label="Tested"
              type="datetime-local"
              value={form.tested_at}
              onChange={(e) => setForm({ ...form, tested_at: e.target.value })}
              required
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { step: 1 } }}
            />
            <TextField
              label="Barcode"
              value={form.barcode}
              onChange={(e) => setForm({ ...form, barcode: e.target.value })}
              helperText="Serial with the stage letter in front, e.g. T123456"
              required
            />
            <TextField
              select
              label="User"
              value={form.user}
              onChange={(e) => setForm({ ...form, user: e.target.value })}
              required
            >
              {users.map((u) => (
                <MenuItem key={u.id} value={u.name}>
                  {u.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Station"
              type="number"
              value={form.station}
              onChange={(e) => setForm({ ...form, station: e.target.value })}
              required
              slotProps={{ htmlInput: { step: 1 } }}
            />
            {NUMBER_FIELDS.map(([field, label]) => (
              <TextField
                key={field}
                label={label}
                type="number"
                value={form[field]}
                onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                helperText="Leave blank if not measured"
                slotProps={{ htmlInput: { step: 'any' } }}
              />
            ))}
          </>
        )}
      </FormDialog>

      <ConfirmDialog
        open={deleting !== null}
        title="Delete test?"
        message={`Delete the ${deleting?.serial} test from ${formatDateTime(deleting?.tested_at)}? This can't be undone.`}
        onClose={() => setDeleting(null)}
        onConfirm={async () => {
          if (deleting) await testsApi.remove(deleting.id);
          await load(serial);
        }}
      />
    </>
  );
}
